import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { parseDecimalInput } from 'utils/stringUtils'

type UseBidPriceVolumeParams = {
  type?: 'spot' | 'block' | 'profile'
  initialPrice: number
  initialVolume: number
}

export type BidField = {
  value: string
  error: string | undefined
  onChange: (value: string) => void
  getNumeric: () => number
  validate: () => boolean
}

export type UseBidPriceVolumeResult = {
  price: BidField
  volume: BidField
}

export function useBidPriceVolume({
  type,
  initialPrice,
  initialVolume,
}: UseBidPriceVolumeParams): UseBidPriceVolumeResult {
  const { t } = useTranslation()
  const [priceInput, setPriceInput] = useState<string>(`${initialPrice}`)
  const [volumeInput, setVolumeInput] = useState<string>(`${initialVolume}`)
  const [priceValidationError, setPriceValidationError] = useState<string>()
  const [volumeValidationError, setVolumeValidationError] = useState<string>()

  function getNumericPrice(): number {
    return parseDecimalInput(priceInput)
  }

  function getNumericVolume(): number {
    return parseDecimalInput(volumeInput)
  }

  function handlePriceChange(value: string): void {
    setPriceInput(value)
    if (priceValidationError) {
      setPriceValidationError(undefined)
    }
  }

  function handleVolumeChange(value: string): void {
    setVolumeInput(value)
    if (volumeValidationError) {
      setVolumeValidationError(undefined)
    }
  }

  function validatePriceBeforeSubmit(): boolean {
    const shouldValidatePrice = type === 'block' || type === 'profile'
    if (!shouldValidatePrice) {
      return true
    }

    const numericPrice = getNumericPrice()
    if (!Number.isFinite(numericPrice) || numericPrice === 0) {
      setPriceValidationError(t('Please enter a price'))
      return false
    }

    setPriceValidationError(undefined)
    return true
  }

  function validateVolumeBeforeSubmit(): boolean {
    const shouldValidateVolume = type === 'block' || type === 'spot'
    if (!shouldValidateVolume) {
      return true
    }

    const numericVolume = getNumericVolume()
    if (!Number.isFinite(numericVolume)) {
      setVolumeValidationError(t('Please enter a volume'))
      return false
    }

    setVolumeValidationError(undefined)
    return true
  }

  return {
    price: {
      value: priceInput,
      error: priceValidationError,
      onChange: handlePriceChange,
      getNumeric: getNumericPrice,
      validate: validatePriceBeforeSubmit,
    },
    volume: {
      value: volumeInput,
      error: volumeValidationError,
      onChange: handleVolumeChange,
      getNumeric: getNumericVolume,
      validate: validateVolumeBeforeSubmit,
    },
  }
}
